import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { arrayOf, func, oneOf, shape, string } from 'prop-types';

import { SelectCompany } from 'Components';

import { SDPLogo } from 'Assets';
import {
  PipelineIcon,
  TransformIcon,
  VisualizeIcon,
  SandboxIcon,
  AddonsIcon,
  UserIcon,
  HomeIcon,
  CatalogIcon,
  InfoIcon,
  MarketPlaceIcon,
  Chat2DashIcon
} from 'Assets/icons';

export default function Sidebar({ navigation, dropDir, actions, user }) {
  const location = useLocation();
  const [collapsedFooter, setCollapsedFooter] = useState(false);

  const collapseFooterEvent = () => setCollapsedFooter((prev) => !prev);

  const RenderOptions = () => {
    // console.log(navigation);
    const mapOptions = (nav) => {
      const isSelected = location?.pathname?.match(nav?.path);
      if (nav.siblings?.length) {
        const isDropActive = nav.siblings
          .map((i) => i.path)
          .includes(location?.pathname);
        return (
          <div
            key={`${nav?.name + 1}`}
            className={`nav-item drop  ${isDropActive ? 'drop--active' : ''}`}
            aria-expanded="true"
          >
            <button
              type="button"
              className="nav-item__header"
              data-testid="sidebar-component_render-options_button"
            >
              <span className="nav-item__title">eee{nav?.name}</span>
              <i
                className={`fas fa-angle-${dropDir} nav-item__icon drop__icon--${dropDir}`}
              />
            </button>
            <div className={`drop__content drop__content--${dropDir}`}>
              {nav.siblings?.map(mapOptions)}
            </div>
          </div>
        );
      }

      const Icons = {
        Home: HomeIcon,
        Pipeline: PipelineIcon,
        'SDI-Pipeline': PipelineIcon,
        Transform: TransformIcon,
        Visualize: VisualizeIcon,
        Sandbox: SandboxIcon,
        'Add-ons': AddonsIcon,
        Catalog: CatalogIcon,
        Exports: InfoIcon,
        Jobs: TransformIcon,
        Marketplace: MarketPlaceIcon,
        Chat2Dash: Chat2DashIcon
      };

      return (
        Icons[nav?.name] !== undefined && (
          <React.Fragment key={nav.name}>
            <Link
              to={nav.path}
              className={`nav-item ${isSelected ? 'nav-item--active' : ''}`}
              key={nav.name}
            >
              <div className="nav-item__icon">
                {Icons[nav?.name] && React.createElement(Icons[nav?.name])}
              </div>
              <span className="nav-item__title">{nav?.name}</span>
            </Link>
          </React.Fragment>
        )
      );
    };
    return navigation?.map(mapOptions);
  };

  const RenderActions = () =>
    actions.map(
      (action) => (
        <li key={action.name}>
          <button
            type="button"
            className="sdp-btn"
            onClick={action.callback}
            data-testid={`sidebar-component_render-actions_button${action.name}`}
          >
            {action.name}
          </button>
        </li>
      ),
      [actions]
    );

  const RenderUserInfo = () => {
    return (
      <div
        className="user-info"
        data-testid="sidebar-component_render-user-info"
      >
        <UserIcon />
        <small>{user.name || 'loading...'}</small>
      </div>
    );
  };

  return (
    <aside className="sdp-sidebar sdp-sidebar" data-testid="sidebar-component">
      <header className="sdp-sidebar__header">
        <div className="sdp-logo-wrapper">
          <img
            src={SDPLogo}
            className="logo-text"
            alt="Semantix Data Platform logo"
            data-testid="sidebar-component_img-logo"
          />
        </div>

        <div className="col-auto flex align-items-center sdp-px-4">
          <SelectCompany iconOnly />
        </div>
      </header>
      <nav className="sdp-sidebar__nav">
        <RenderOptions />
      </nav>
      <footer className="sdp-sidebar__footer">
        <div className="footer-main-content">
          <RenderUserInfo />
          <button
            type="button"
            className="sdp-btn sdp-btn--icon-only sdp-collapse-button"
            onClick={collapseFooterEvent}
          >
            <i
              className={`fas fa-angle-${
                collapsedFooter ? 'down' : 'up'
              } footer-main-content__icon`}
            />
          </button>
        </div>
        <div
          className={`footer-aditional-nav collapse ${
            collapsedFooter ? 'show' : ''
          }`}
        >
          <ul>
            <RenderActions />
          </ul>
        </div>
      </footer>
    </aside>
  );
}

Sidebar.propTypes = {
  navigation: arrayOf(
    shape({
      name: string.isRequired,
      path: string,
      siblings: arrayOf(
        shape({
          name: string.isRequired,
          path: string.isRequired
        })
      )
    })
  ),
  dropDir: oneOf(['down', 'right']),
  actions: arrayOf(
    shape({
      name: string,
      callback: func
    })
  ),
  user: shape({
    name: string
  })
};

Sidebar.defaultProps = {
  navigation: [
    {
      name: 'default',
      path: '/default'
    }
  ],
  dropDir: 'down',
  actions: [{ name: 'sidebar', callback: () => null }],
  user: { name: 'Name' }
};
